import {
  AfterViewInit,
  Component,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { RequestService } from '../../../shared/services/request.service';
import { ComponentService } from '../../../shared/abstract/component.service';
import { ManagementType } from '../../../shared/enum/managementType';
import { ActionType } from '../../../shared/enum/actionType';
import { MatTableDataSource } from '@angular/material/table';
import { ModalComponent } from '../../../shared/widgets/modal/modal.component';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, map, Subject, Subscription } from 'rxjs';
import { environment } from 'environments/environment';
import { WorkRule } from 'shared/model/work-rule-model';

interface SearchParams {
  searchTerm: string;
  columnName?: string;
}

@Component({
  selector: 'app-business-rules',
  templateUrl: './business-rules.component.html',
  styleUrl: './business-rules.component.css',
})
export class BusinessRulesComponent
  extends ComponentService<any>
  implements OnInit, AfterViewInit, OnDestroy
{
  dataSource = new MatTableDataSource<any>();
  currentCompany;
  url;
  businessRules: WorkRule[] = [];
  pagedBusinessRules: WorkRule[] = [];
  filteredBusinessRules: WorkRule[] | null = null;
  pageIndex = 0;
  pageSize = 5;
  currentPage = 0;
  totalPages = 0;
  maxVisibleButtons = 5;
  Math = Math;

  isSideNavOpen = false;
  selectedManagementType!: ManagementType;
  selectedActionType!: ActionType;
  selectedBusinessRule?: any;

  ManagementType = ManagementType;
  ActionType = ActionType;

  displayedColumns = ['ruleKey','ruleDescription', 'options'];

  @ViewChild('ruleKeyFilter') ruleKeyFilter?: ElementRef<HTMLInputElement>;
  @ViewChild('ruleDescFilter') ruleDescFilter?: ElementRef<HTMLInputElement>;

  constructor(
    private _request: RequestService,
    injector: Injector,
    private _dialog: MatDialog
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this._appsService.projectChecedkWorkRule = false;
    if (
      this._appsService.existCurrentProject?.companyId &&
      this._appsService.existCurrentProject?.projectId
    ) {
      this.currentProject = this._appsService.existCurrentProject;
      this.currentCompany = this._appsService.existCurrentCompany;
      this.managementConfig();
    }

    this.cPSubs = this._appsService.currentProject.subscribe((project) => {
      this.currentProject = project;
      this.managementConfig();
    });
    this.url = environment.apiUrl + '/';
  }

  ngOnDestroy(): void {
    this.cPSubs ? this.cPSubs.unsubscribe() : false;
  }

  ngAfterViewInit(): void {}

  managementConfig() {
    this.getBusinessRules();
  }

  projectToggle(event: any) {
    if (event.checked) {
      this._appsService.projectChecedkWorkRule = true;
    } else {
      this._appsService.projectChecedkWorkRule = false;
    }

    if (this.ruleKeyFilter && this.ruleKeyFilter.nativeElement) {
      this.ruleKeyFilter.nativeElement.value = '';
    }
    if (this.ruleDescFilter && this.ruleDescFilter.nativeElement) {
      this.ruleDescFilter.nativeElement.value = '';
    }
    
    this.filteredBusinessRules = null;
    this.currentPage = 0;
    this.pageIndex = 0;
    this.getBusinessRules();
  }

  getBusinessRules() {
    if (this._appsService.projectChecedkWorkRule == true) {
      this._request
        .getWorkRules(this.currentCompany, this.currentProject.projectId)
        .then((res) => {
          if (res) {
            this.businessRules = res;
          }
        })
        .catch((err) => {
          this.MessageBox(
            this.languageKey('unexpectedError'),
            'warn',
            'center'
          );
        })
        .finally(() => {
          this.filteredBusinessRules = null;
          this.updateTotalPages();
          this.currentPage = 0;
          this.pageIndex = 0;
          this.updatePagedData();
        });
  } else {
      this._request
        .getWorkRules(this.currentCompany, null)
        .then((res) => {
          if (res) {
            this.businessRules = res;
          }
        })
        .catch((err) => {
          this.MessageBox(
            this.languageKey('unexpectedError'),
            'warn',
            'center'
          );
        })
        .finally(() => {
          this.filteredBusinessRules = null;
          this.updateTotalPages();
          this.currentPage = 0;
          this.pageIndex = 0;
          this.updatePagedData();
        });
    }
  }

  // PAGINATE
  get visiblePageNumbers(): number[] {
    const start = Math.max(
      0,
      Math.min(
        this.currentPage - Math.floor(this.maxVisibleButtons / 2),
        this.totalPages - this.maxVisibleButtons
      )
    );
    return Array.from(
      { length: Math.min(this.maxVisibleButtons, this.totalPages) },
      (_, i) => start + i + 1
    );
  }

  onPageChange(pageIndex: number) {
    if (pageIndex >= 0 && pageIndex < this.totalPages) {
      this.currentPage = pageIndex;
    }
    this.pageIndex = pageIndex;
    this.updatePagedData();
  }

  updatePagedData(filteredBusinessRules: WorkRule[] | null = null) {
    const startIndex = this.currentPage * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    const source =
      filteredBusinessRules ?? this.filteredBusinessRules ?? this.businessRules;
    this.pagedBusinessRules = source.slice(startIndex, endIndex);
    this.dataSource = new MatTableDataSource(this.pagedBusinessRules);
  }

  updatePageSize(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    this.pageSize = parseInt(selectElement.value, 10);
    this.updateTotalPages();
    this.currentPage = 0;
    this.pageIndex = 0;
    this.updatePagedData();
  }

  updateTotalPages() {
    const length = this.filteredBusinessRules
      ? this.filteredBusinessRules.length
      : this.businessRules.length;
    this.totalPages = Math.ceil(length / this.pageSize) || 0;
  }

  deleteBusinessRule(element) {
    const dialogRef = this._dialog.open(ModalComponent, {
      width: '400px',
      data: {
        type: 'confirm',
        title: 'deleteBusinessRule',
        body: 'deleteBusinessRuleConfirm',
        ok: 'confirm',
        cancel: 'cancel',
        color: 'warn',
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (this._appsService.projectChecedkWorkRule == true) {
          this._request
            .deleteWorkRule(
              element.id,
              this.currentCompany,
              this._appsService.existCurrentProject.projectId
            )
            .then((res) => {
              if (res.code == 1) {
                this.dataSource.data = this.dataSource.data.filter(
                  (u) => u.id != element.id
                );
                this.MessageBox(res.message ?? '', 'primary', 'center');
              } else if (res.code == 2) {
                this.MessageBox(res.message ?? '', 'orange', 'center');
              } else {
                this.MessageBox(res.message ?? '', 'warn', 'center');
              }
            })
            .catch((err) => {
              this.MessageBox(
                this.langauge('unexpectedError'),
                'warn',
                'center'
              );
            })
            .finally(() => {
              this.getBusinessRules();
            });
        } else {
          this._request
            .deleteWorkRule(element.id, this.currentCompany, null)
            .then((res) => {
              if (res.code == 1) {
                this.dataSource.data = this.dataSource.data.filter(
                  (u) => u.id != element.id
                );
                this.MessageBox(res.message ?? '', 'primary', 'center');
              } else if (res.code == 2) {
                this.MessageBox(res.message ?? '', 'orange', 'center');
              } else {
                this.MessageBox(res.message ?? '', 'warn', 'center');
              }
            })
            .catch((err) => {
              this.MessageBox(
                this.languageKey('unexpectedError'),
                'warn',
                'center'
              );
            })
            .finally(() => {
              this.getBusinessRules();
            });
        }
      }
    });
    this._appsService.logInfo("İş kuralı silindi");
  }

  openSideNav(
    managementType: ManagementType,
    actionType: ActionType,
    businessRule?: any
  ) {
    this.selectedManagementType = managementType;
    this.selectedActionType = actionType;
    this.isSideNavOpen = true;
    this.selectedBusinessRule = businessRule ? { ...businessRule } : null;
    this._appsService.logInfo("İş kuralı " + this.languageKey(actionType) + " açıldı");
  }

  applyFilterBussinessRule(filterValue, dataSource: any, type?, objectType?) {
    const comingValue = filterValue.target.value?.toString() ?? '';
    if (comingValue && comingValue.trim() !== '') {
      const filtered = this.businessRules.filter((x) =>
        x.ruleKey?.toLocaleLowerCase().includes(comingValue.toLocaleLowerCase())
      );
      this.filteredBusinessRules = filtered;
      this.currentPage = 0;
      this.pageIndex = 0;
      this.updateTotalPages();
      this.updatePagedData(this.filteredBusinessRules);
    } else {
      this.filteredBusinessRules = null;
      this.currentPage = 0;
      this.pageIndex = 0;
      this.updateTotalPages();
      this.updatePagedData();
    }
  }
}
